import { Organization } from "../Interfaces"
import api from "./api"
import { AxiosError } from "axios"
import { sortByAttribute } from "../utils/helper"
// Test

class OrganizationService {

  private baseUrl = "/organization";

  public create = async (organizationInfo: any, imageFile: File | null = null) => {
    if (!organizationInfo) throw new Error("No organization info provided")
    try {
      const response = await api.post(
        `${this.baseUrl}`,
        organizationInfo,
      );
      if (imageFile) await this.uploadLogo(imageFile, response.data.id, response.data.name)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return null;
    }
  }

  public uploadLogo = async (imageFile: File, orgId: number, orgName: string) => {
    if (!imageFile) return;
    try {
      const { data: signedUrl } = await api.get(
        `${this.baseUrl}/update/logo/${orgId}/${orgName}`,
      )
      if (imageFile.type !== "image/jpeg" && imageFile.type !== "image/png" && imageFile.type !== "image/jpg") throw new Error("Invalid file type")
      const response = await api.put(
        signedUrl,
        imageFile,
        {
          headers: {
            "Content-Type": imageFile.type,
            "Authorization": undefined,
          }
        }
      );
      return response.data;
    } catch (error) {
      return error as Error;
    }
  };

  public getViewableLogo = async (orgId: number, orgName: string) => {
    try {
      const { data: signedUrl } = await api.get(
        `${this.baseUrl}/logo/${orgId}/${orgName}`,
      )
      return signedUrl;
    } catch (error) {
      return error as Error;
    }
  }

  public getOne = async (id: number) => {
    if (!id) throw new Error("No id provided")
    try {
      const response = await api.get(
        `${this.baseUrl}/${id}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return null;
    }
  }

  public getManagementCompanyName = async (id: number) => {
    if (!id) throw new Error("No id provided")
    try {
      const response = await api.get(
        `${this.baseUrl}/managementCompany-name/${id}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return null;
    }
  }

  public getAllByUser = async (userId: number): Promise<Partial<Organization[]>> => {
    try {
      const response = await api.get(
        `${this.baseUrl}/organizations/${userId}`
      );
      sortByAttribute(response.data, "name")
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return [];
    }
  }

  public getAllByManagementCompany = async (managementCompanyId: number): Promise<Partial<Organization[]>> => {
    try {
      const response = await api.get(
        `${this.baseUrl}/all/by-management-company/${managementCompanyId}`
      )
      sortByAttribute(response.data, "name")
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return [];
    }
  }

  public getSomeByUser = async (userId: number, limit: number, page: number, searchTerm?: string): Promise<{ organizations: Partial<Organization[]>, totalCount: number }> => {
    try {
      const response = await api.get(
        `${this.baseUrl}/some/by-user/${userId}?limit=${limit}&offset=${page}&searchTerm=${searchTerm || ""}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return { organizations: [], totalCount: 0 };
    }
  }

  public update = async (organizationInfo: any, imageFile: File | null = null) => {
    if (!organizationInfo) throw new Error("No area info provided")

    try {
      const response = await api.put(
        `${this.baseUrl}`,
        organizationInfo
      );
      if (imageFile) await this.uploadLogo(imageFile, response.data.id, response.data.name)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return null;
    }
  }

  public delete = async (id: number) => {
    if (!id) throw new Error("No id provided")
    try {
      const response = await api.delete(
        `${this.baseUrl}/${id}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return null;
    }
  }
}

export const organizationService = new OrganizationService()

